import { usePlantCatalog } from '../context/PlantCatalogContext'
import type { Container } from '../types'
import { CONTAINER_SIZE_INFO } from '../types'

interface ContainerVisualizerProps {
  container: Container
  onRemovePlant?: (plantId: string) => void
  mini?: boolean
}

const SIZE_DIMS: Record<string, { w: number; h: number }> = {
  small:  { w: 120, h: 140 },
  medium: { w: 180, h: 180 },
  large:  { w: 240, h: 210 },
  xlarge: { w: 320, h: 160 },
}

const POT_COLORS: Record<string, string> = {
  balcony:    '#e8c8a0',
  patio:      '#d4b896',
  windowsill: '#c8d8a8',
  indoors:    '#d0b8e8',
  yard:       '#c8d8b0',
}

export default function ContainerVisualizer({ container, onRemovePlant, mini = false }: ContainerVisualizerProps) {
  const { getPlantById } = usePlantCatalog()
  const plants = container.plantIds.map(id => getPlantById(id)).filter(Boolean)
  const thrillers = plants.filter(p => p!.role.includes('thriller'))
  const fillers   = plants.filter(p => !p!.role.includes('thriller') && !p!.role.includes('spiller'))
  const spillers  = plants.filter(p => p!.role.includes('spiller') && !p!.role.includes('thriller'))

  const dims = mini
    ? { w: SIZE_DIMS[container.size].w * 0.6, h: SIZE_DIMS[container.size].h * 0.6 }
    : SIZE_DIMS[container.size]

  const potColor = POT_COLORS[container.location] ?? '#e0c8a0'
  const isWindowBox = container.shape === 'window-box' || container.size === 'xlarge'
  const potW = dims.w
  const potH = dims.h * 0.45
  const potY = dims.h * 0.5
  const potTopW = potW * 0.88

  const sizeInfo = CONTAINER_SIZE_INFO[container.size]

  return (
    <div className="flex flex-col items-center gap-2">
      <svg
        width={dims.w}
        height={dims.h + 20}
        viewBox={`0 0 ${dims.w} ${dims.h + 20}`}
        className="overflow-visible"
      >
        {/* Spillers — cascade below rim */}
        {spillers.map((p, i) => {
          const x = dims.w * 0.15 + (i * dims.w * 0.25) % (dims.w * 0.7)
          return (
            <g key={`spill-${i}`}>
              <ellipse cx={x} cy={potY + potH * 0.1} rx={14} ry={20} fill={p!.accentColor + '90'} />
              <text x={x} y={potY + potH * 0.1 + 6} textAnchor="middle" fontSize={mini ? 12 : 16}>{p!.emoji}</text>
              <ellipse cx={x + 18} cy={potY + potH * 0.2} rx={10} ry={14} fill={p!.accentColor + '70'} />
            </g>
          )
        })}

        {/* Pot body */}
        {isWindowBox ? (
          <rect x={0} y={potY} width={potW} height={potH} rx={8} fill={potColor} stroke="#3d2b1f" strokeWidth={2} />
        ) : (
          <path
            d={`M ${(potW - potTopW) / 2} ${potY}
                L ${(potW + potTopW) / 2} ${potY}
                L ${potW * 0.85} ${potY + potH}
                L ${potW * 0.15} ${potY + potH} Z`}
            fill={potColor}
            stroke="#3d2b1f"
            strokeWidth={2}
          />
        )}

        {/* Pot rim highlight */}
        <ellipse
          cx={potW / 2}
          cy={potY}
          rx={potTopW / 2}
          ry={isWindowBox ? 6 : 8}
          fill={potColor}
          stroke="#3d2b1f"
          strokeWidth={2}
        />

        {/* Drainage hole hint */}
        <ellipse cx={potW / 2} cy={potY + potH - 4} rx={8} ry={3} fill="#3d2b1f40" />

        {/* Soil surface */}
        <ellipse cx={potW / 2} cy={potY + 4} rx={potTopW / 2 - 6} ry={isWindowBox ? 4 : 6} fill="#7a5c44" />

        {/* Fillers in pot */}
        {fillers.map((p, i) => {
          const spread = fillers.length > 1 ? (i / (fillers.length - 1) - 0.5) * potTopW * 0.6 : 0
          const cx = potW / 2 + spread
          const cy = potY - 12
          return (
            <g key={`fill-${i}`}>
              <ellipse cx={cx} cy={cy} rx={22} ry={16} fill={p!.accentColor + 'cc'} />
              <text x={cx} y={cy + 6} textAnchor="middle" fontSize={mini ? 14 : 18}>{p!.emoji}</text>
            </g>
          )
        })}

        {/* Thriller — tall, centered, above fillers */}
        {thrillers.map((p, i) => {
          const cx = potW / 2 + (i - (thrillers.length - 1) / 2) * 20
          const stemTop = potY - (mini ? 45 : 65)
          return (
            <g key={`thrill-${i}`}>
              <line x1={cx} y1={potY - 5} x2={cx} y2={stemTop + 10} stroke="#5cb85c" strokeWidth={3} />
              <ellipse cx={cx} cy={stemTop} rx={18} ry={14} fill={p!.accentColor + 'cc'} />
              <text x={cx} y={stemTop + 6} textAnchor="middle" fontSize={mini ? 14 : 20}>{p!.emoji}</text>
            </g>
          )
        })}

        {/* Empty pot prompt */}
        {plants.length === 0 && (
          <text x={potW / 2} y={potY - 10} textAnchor="middle" fontSize={mini ? 10 : 12} fill="#7a5c44" fontFamily="Nunito">
            + add plants
          </text>
        )}
      </svg>

      {/* Container label */}
      {!mini && (
        <div className="text-center space-y-1">
          <p className="font-body font-bold text-ac-brown text-sm">{container.nickname}</p>
          <p className="font-body text-xs text-ac-brown-light">{sizeInfo.label} · {sizeInfo.inches}</p>
          <p className="font-body text-xs text-ac-brown-light capitalize">{container.sunExposure.replace('-', ' ')} · {container.location}</p>
        </div>
      )}

      {/* Plant remove buttons */}
      {!mini && onRemovePlant && plants.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center max-w-xs">
          {plants.map(p => (
            <button
              key={p!.id}
              onClick={() => onRemovePlant(p!.id)}
              className="inline-flex items-center gap-1 text-xs font-body bg-ac-cream border-2 border-ac-brown rounded-full px-2 py-0.5 hover:bg-ac-pink/30 transition-colors"
              title={`Remove ${p!.name}`}
            >
              {p!.emoji} {p!.name} ✕
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
